<template>
  <div
    id="canvasControlsNav"
    class="absolute bottom-0 left-1/2 z-30 m-2 flex -translate-x-1/2"
    :class="{
      'cursor-not-allowed opacity-50': isLocked,
    }">
    <div
      v-if="enableFloatingPanel"
      ref="centerPanel"
      id="centerPanel"
      class="absolute bottom-0 left-1/2 z-30 m-2 flex -translate-x-1/2 rounded-lg bg-gray-800 p-2 shadow">
      <DButton @click="select" text v-tooltip.top="'Select (Q)'"
        ><MousePointer
          size="16"
          stroke-width="1.8"
          class="hover:text-primary text-gray-600"
          :class="selectionStore.selectedObjectTransformType === null ? 'text-primary' : ''"
      /></DButton>

      <div class="mx-2 h-10 w-px shrink-0 bg-gray-950"></div>

      <DButton @click="move" text v-tooltip.top="'Move (E)'" :disabled="isLocked"
        ><Move
          size="16"
          stroke-width="1.8"
          class="hover:text-primary text-gray-600"
          :class="selectionStore.selectedObjectTransformType === 'translate' ? 'text-primary' : ''"
      /></DButton>

      <div class="mx-2 h-10 w-px shrink-0 bg-gray-950"></div>

      <DButton @click="rotate" text v-tooltip.top="'Rotate (R)'" :disabled="isLocked"
        ><RefreshCw
          size="16"
          stroke-width="1.8"
          class="hover:text-primary text-gray-600"
          :class="selectionStore.selectedObjectTransformType === 'rotate' ? 'text-primary' : ''"
      /></DButton>

      <div class="mx-2 h-10 w-px shrink-0 bg-gray-950"></div>

      <DButton @click="scale" text v-tooltip.top="'Scale (T)'" :disabled="isLocked"
        ><Maximize2
          size="16"
          stroke-width="1.8"
          class="hover:text-primary text-gray-600"
          :class="selectionStore.selectedObjectTransformType === 'scale' ? 'text-primary' : ''"
      /></DButton>

      <template v-if="object && object.type === 'projector'">
        <div class="mx-2 h-10 w-px shrink-0 bg-gray-950"></div>

        <DButton @click="world" text v-tooltip.top="'Toggle Local/World (X)'" :disabled="isLocked"
          ><Globe
            v-if="selectionStore.selectedObjectTransformWorld !== 'local'"
            &&
            !isLocked
            size="16"
            stroke-width="1.8"
            class="text-primary" />
          <Move3d
            v-if="selectionStore.selectedObjectTransformWorld === 'local'"
            &&
            !isLocked
            size="16"
            stroke-width="1.8"
            class="text-primary" />
        </DButton>

        <div class="mx-2 h-10 w-px shrink-0 bg-gray-950"></div>

        <DButton @click="target" text v-tooltip.top="'Toggle Target/Position (Y)'"
          ><Target
            size="16"
            stroke-width="1.8"
            class="hover:text-primary text-gray-600"
            :class="selectionStore.selectedObjectTransformTarget ? 'text-primary' : ''" />
        </DButton>
      </template>
    </div>
  </div>
</template>

<script setup>
import { useNodeManager } from '@/composables/useNodeManager';
import { useSelectionStore } from '@/stores/ui/selection';
import { changeTransformControlsAll, changeTransformControlsWorld } from '@/threejs/handlers/transform';
import { computed } from 'vue';

import { DButton } from '@disguise/cloud-ui';
import { Globe, Maximize2, MousePointer, Move, Move3d, RefreshCw, Target } from 'lucide-vue-next';

const selectionStore = useSelectionStore();

const { findNodeById } = useNodeManager();

const object = computed(() => {
  if (!selectionStore.selectedObjectId) return null;
  return findNodeById(selectionStore.selectedObjectId);
});

const props = defineProps({
  controlsRef: {
    type: Object,
  },
  leftSidebarWidth: {
    type: Number,
    default: 0,
  },
  rightSidebarWidth: {
    type: Number,
    default: 0,
  },
});

const isLocked = computed(() => {
  if (selectionStore.selectedObjectId) {
    const node = findNodeById(selectionStore.selectedObjectId);
    return node?.data?.lock || false;
  }
  return false;
});

const select = () => {
  selectionStore.selectedObjectTransformType = null;
};

const move = () => {
  selectionStore.selectedObjectTransformType = 'translate';
  selectionStore.selectedObjectTransformTarget = false;

  changeTransformControlsAll(selectionStore.selectedObjectTransformType);
};

const rotate = () => {
  selectionStore.selectedObjectTransformType = 'rotate';
  changeTransformControlsAll(selectionStore.selectedObjectTransformType);
};

const scale = () => {
  selectionStore.selectedObjectTransformType = 'scale';
  changeTransformControlsAll(selectionStore.selectedObjectTransformType);
};

const world = () => {
  if (selectionStore.selectedObjectTransformWorld !== 'world') {
    selectionStore.selectedObjectTransformWorld = 'world';
    changeTransformControlsWorld(selectionStore.selectedObjectTransformWorld);
  } else {
    selectionStore.selectedObjectTransformWorld = 'local';
    changeTransformControlsWorld(selectionStore.selectedObjectTransformWorld);
  }
};

const target = () => {
  selectionStore.selectedObjectTransformType = 'translate';
  selectionStore.selectedObjectTransformTarget = !selectionStore.selectedObjectTransformTarget;
};

const enableFloatingPanel = computed(() => {
  // console.log(selectionStore.selectedObjectId);
  // console.log(object.value?.type);
  return (
    selectionStore.selectedObjectId &&
    object.value &&
    object.value.type &&
    !['measurement', 'camera', 'annotation'].includes(object.value.type)
  );
});
</script>
